import type { AxiosRequestConfig } from "axios";
import { api } from "../utils/axios";
import { normalizeRestaurantPayload } from "../utils/restaurantPayload";

export class ReservationApiError extends Error {
  status?: number;
  details?: unknown;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = "ReservationApiError";
    this.status = status;
    this.details = details;
  }
}

type RequestOptions = {
  token?: string | null;
};

type DashboardMetrics = {
  confirmedBookings: number;
  cancelledBookings: number;
  totalCovers: number;
  noShows: number;
  totalBookings: number;
  pendingBookings: number;
  completedBookings: number;
  walkInBookings: number;
};

export type Section = {
  _id: string;
  restaurantId: string;
  name: string;
  description?: string;
  color?: string;
  isActive: boolean;
  isArchived?: boolean;
};

export type Table = {
  _id: string;
  restaurantId: string;
  sectionId: string;
  name: string;
  capacity: number;
  combinableWith?: string[];
  notes?: string;
  isActive?: boolean;
  isArchived?: boolean;
  layout?: {
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    rotation?: number;
    locked?: boolean;
    zIndex?: number;
    shape?: "circle" | "square" | "rectangle";
  };
};

export type ReservationRecord = {
  _id: string;
  guestName: string;
  phone: string;
  email?: string;
  occasion?: string;
  specialRequest?: string;
  acceptUpdates?: boolean;
  partySize: number;
  startTime: string;
  endTime: string;
  status: "pending" | "confirmed" | "seated" | "completed" | "cancelled" | "no_show" | "waitlist";
  source?: string;
  notesInternal?: string;
  createdAt?: string;
  updatedAt?: string;
  seatedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  assignedBy?: string;
};

export type ReservationDashboardResponse = {
  success: true;
  dashboardDate: string;
  timezoneUsed: string;
  metrics: DashboardMetrics;
  todaysOverview: {
    date: string;
    bookings: Array<Pick<ReservationRecord, "_id" | "guestName" | "partySize" | "status" | "startTime">>;
  };
};

type TableGroup = {
  _id: string;
  tableIds: Array<string | { _id: string; name: string }>;
  capacity: number;
  notes?: string;
  isActive: boolean;
};

export type ReservationTableAssignment = {
  _id: string;
  reservationId: string;
  tableId?: { _id: string; name: string };
  tableGroupId?: { _id: string };
  assignedBy?: string;
};

const withAuth = (options?: RequestOptions): AxiosRequestConfig => {
  if (!options?.token) return { withCredentials: true };

  return {
    withCredentials: true,
    headers: {
      Authorization: `Bearer ${options.token}`,
    },
  };
};

export const toApiError = (error: any, fallbackMessage: string): ReservationApiError => {
  const status = error?.response?.status;
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.formattedMessage ||
    fallbackMessage;

  return new ReservationApiError(message, status, error?.response?.data);
};

const reservationsBase = (restaurantId: string) => `/api/reservations/${restaurantId}`;

export const getSecureRestaurant = async (restaurantId: string, token?: string | null) => {
  try {
    const response = await api.get(`/api/restaurants/private/${restaurantId}/secure`, withAuth({ token }));
    return normalizeRestaurantPayload(response.data);
  } catch (error) {
    throw toApiError(error, "Failed to load restaurant details.");
  }
};

export const getDashboard = async (
  restaurantId: string,
  options?: { date?: string } & RequestOptions
): Promise<ReservationDashboardResponse> => {
  try {
    const response = await api.get<ReservationDashboardResponse>(`${reservationsBase(restaurantId)}/reservations/dashboard`, {
      ...withAuth(options),
      params: options?.date ? { date: options.date } : undefined,
    });

    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch reservation dashboard.");
  }
};

export const createSection = async (
  restaurantId: string,
  body: { name?: string; description?: string; isActive?: boolean },
  options?: RequestOptions
): Promise<{ success: true; section: Section }> => {
  try {
    const response = await api.post(`${reservationsBase(restaurantId)}/sections`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to create section.");
  }
};

export const listSections = async (
  restaurantId: string,
  options?: RequestOptions
): Promise<{ success: true; count: number; sections: Section[] }> => {
  try {
    const response = await api.get(`${reservationsBase(restaurantId)}/sections`, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch sections.");
  }
};

export const updateSection = async (
  restaurantId: string,
  sectionId: string,
  body: { name?: string; description?: string; isActive?: boolean },
  options?: RequestOptions
): Promise<{ success: true; section: Section }> => {
  try {
    const response = await api.patch(`${reservationsBase(restaurantId)}/sections/${sectionId}`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to update section.");
  }
};

export const archiveSection = async (
  restaurantId: string,
  sectionId: string,
  options?: RequestOptions
): Promise<{ success: true; message: string; section: Section }> => {
  try {
    const response = await api.delete(`${reservationsBase(restaurantId)}/sections/${sectionId}`, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to archive section.");
  }
};

export const createTable = async (
  restaurantId: string,
  body: {
    name: string;
    capacity: number;
    sectionId: string;
    combinableWith?: string[];
    isActive?: boolean;
    notes?: string;
    layout?: Table["layout"];
  },
  options?: RequestOptions
): Promise<{ success: true; table: Table }> => {
  try {
    const response = await api.post(`${reservationsBase(restaurantId)}/tables`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to create table.");
  }
};

export const listTables = async (
  restaurantId: string,
  query?: { sectionId?: string; active?: boolean },
  options?: RequestOptions
): Promise<{ success: true; count: number; tables: Table[] }> => {
  try {
    const response = await api.get(`${reservationsBase(restaurantId)}/tables`, {
      ...withAuth(options),
      params: query,
    });

    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch tables.");
  }
};

export const getTable = async (
  restaurantId: string,
  tableId: string,
  options?: RequestOptions
): Promise<{ success: true; table: Table }> => {
  try {
    const response = await api.get(`${reservationsBase(restaurantId)}/tables/${tableId}`, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch table.");
  }
};

export const updateTable = async (
  restaurantId: string,
  tableId: string,
  body: Partial<Pick<Table, "name" | "capacity" | "sectionId" | "isActive" | "combinableWith" | "notes" | "layout">>,
  options?: RequestOptions
): Promise<{ success: true; table: Table }> => {
  try {
    const response = await api.patch(`${reservationsBase(restaurantId)}/tables/${tableId}`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to update table.");
  }
};

export const archiveTable = async (
  restaurantId: string,
  tableId: string,
  options?: RequestOptions
): Promise<{ success: true; message: string; table: Table }> => {
  try {
    const response = await api.delete(`${reservationsBase(restaurantId)}/tables/${tableId}`, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to archive table.");
  }
};

export const mergeTables = async (
  restaurantId: string,
  body: { tableIds: string[]; notes?: string },
  options?: RequestOptions
): Promise<{ success: true; tableGroup: TableGroup }> => {
  try {
    const response = await api.post(`${reservationsBase(restaurantId)}/tables/merge`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to merge tables.");
  }
};

export const unmergeTables = async (
  restaurantId: string,
  body: { tableGroupId: string },
  options?: RequestOptions
): Promise<{ success: true; message: string; tableGroup: TableGroup }> => {
  try {
    const response = await api.post(`${reservationsBase(restaurantId)}/tables/unmerge`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to unmerge tables.");
  }
};

export const createReservation = async (
  restaurantId: string,
  body: {
    guestName: string;
    phone: string;
    partySize: number;
    startTime: string;
    endTime: string;
    email?: string;
    occasion?: string;
    specialRequest?: string;
    acceptUpdates?: boolean;
    status?: ReservationRecord["status"];
    source?: string;
    notesInternal?: string;
  },
  options?: RequestOptions
): Promise<{ success: true; reservation: ReservationRecord }> => {
  try {
    const response = await api.post(`${reservationsBase(restaurantId)}/reservations`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to create reservation.");
  }
};

export const listReservations = async (
  restaurantId: string,
  query?: { date?: string; status?: string; guest?: string },
  options?: RequestOptions
): Promise<{ success: true; count: number; reservations: ReservationRecord[] }> => {
  try {
    const response = await api.get(`${reservationsBase(restaurantId)}/reservations`, {
      ...withAuth(options),
      params: query,
    });
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch reservations.");
  }
};

export const getReservation = async (
  restaurantId: string,
  reservationId: string,
  options?: RequestOptions
): Promise<{ success: true; reservation: ReservationRecord }> => {
  try {
    const response = await api.get(`${reservationsBase(restaurantId)}/reservations/${reservationId}`, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch reservation.");
  }
};

export const updateReservation = async (
  restaurantId: string,
  reservationId: string,
  body: Partial<ReservationRecord>,
  options?: RequestOptions
): Promise<{ success: true; reservation: ReservationRecord }> => {
  try {
    const response = await api.patch(
      `${reservationsBase(restaurantId)}/reservations/${reservationId}`,
      body,
      withAuth(options)
    );
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to update reservation.");
  }
};

const reservationStatusAction = async (
  restaurantId: string,
  reservationId: string,
  action: "seated" | "assign-staff" | "completed" | "no-show" | "cancel",
  body?: Record<string, unknown>,
  options?: RequestOptions
): Promise<{ success: true; reservation: ReservationRecord }> => {
  try {
    const response = await api.patch(
      `${reservationsBase(restaurantId)}/reservations/${reservationId}/${action}`,
      body ?? {},
      withAuth(options)
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, `Failed to mark reservation as ${action}.`);
  }
};

export const toggleOlineReservations = async (
  restaurantId: string,
  body: { allowOnlineReservations: boolean },
): Promise<{ success: true; reservation: ReservationRecord }> => {
  try {
    const response = await api.patch(
      `/api/restaurants/private/${restaurantId}/online-reservations`,
      { allowOnlineReservations: body.allowOnlineReservations },
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, `Failed to update online reservations setting.`);
  }
};

export const markReservationSeated = (restaurantId: string, reservationId: string, options?: RequestOptions) =>
  reservationStatusAction(restaurantId, reservationId, "seated", undefined, options);

export const assignReservationStaff = (
  restaurantId: string,
  reservationId: string,
  body?: { staffId?: string },
  options?: RequestOptions
) => reservationStatusAction(restaurantId, reservationId, "assign-staff", body, options);

export const markReservationCompleted = (restaurantId: string, reservationId: string, options?: RequestOptions) =>
  reservationStatusAction(restaurantId, reservationId, "completed", undefined, options);

export const markReservationNoShow = (restaurantId: string, reservationId: string, options?: RequestOptions) =>
  reservationStatusAction(restaurantId, reservationId, "no-show", undefined, options);

export const cancelReservation = (restaurantId: string, reservationId: string, options?: RequestOptions) =>
  reservationStatusAction(restaurantId, reservationId, "cancel", undefined, options);

export const assignReservationTables = async (
  restaurantId: string,
  reservationId: string,
  body: { tableIds?: string[]; tableGroupId?: string; assignedBy?: string },
  options?: RequestOptions
): Promise<{ success: true; count: number; assignments: ReservationTableAssignment[] }> => {
  try {
    const response = await api.post(
      `${reservationsBase(restaurantId)}/reservations/${reservationId}/tables`,
      body,
      withAuth(options)
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to assign tables to reservation.");
  }
};

export const listReservationTableAssignments = async (
  restaurantId: string,
  reservationId: string,
  options?: RequestOptions
): Promise<{ success: true; count: number; assignments: ReservationTableAssignment[] }> => {
  try {
    const response = await api.get(
      `${reservationsBase(restaurantId)}/reservations/${reservationId}/tables`,
      withAuth(options)
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch reservation table assignments.");
  }
};

export const removeReservationTableAssignment = async (
  restaurantId: string,
  assignmentId: string,
  options?: RequestOptions
): Promise<{ success: true; message: string }> => {
  try {
    const response = await api.delete(
      `${reservationsBase(restaurantId)}/reservation-table-assignments/${assignmentId}`,
      withAuth(options)
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to remove table assignment.");
  }
};

export const createTableGroup = async (
  restaurantId: string,
  body: { tableIds: string[]; notes?: string },
  options?: RequestOptions
): Promise<{ success: true; tableGroup: TableGroup }> => {
  try {
    const response = await api.post(`${reservationsBase(restaurantId)}/table-groups`, body, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to create table group.");
  }
};

export const listTableGroups = async (
  restaurantId: string,
  query?: { active?: "true" | "false" | "all" },
  options?: RequestOptions
): Promise<{ success: true; count: number; tableGroups: TableGroup[] }> => {
  try {
    const response = await api.get(`${reservationsBase(restaurantId)}/table-groups`, {
      ...withAuth(options),
      params: query,
    });
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch table groups.");
  }
};

export const getTableGroup = async (
  restaurantId: string,
  tableGroupId: string,
  options?: RequestOptions
): Promise<{ success: true; tableGroup: TableGroup }> => {
  try {
    const response = await api.get(`${reservationsBase(restaurantId)}/table-groups/${tableGroupId}`, withAuth(options));
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to fetch table group.");
  }
};

export const unmergeTableGroup = async (
  restaurantId: string,
  tableGroupId: string,
  options?: RequestOptions
): Promise<{ success: true; message: string; tableGroup: TableGroup }> => {
  try {
    const response = await api.patch(
      `${reservationsBase(restaurantId)}/table-groups/${tableGroupId}/unmerge`,
      {},
      withAuth(options)
    );
    return response.data;
  } catch (error) {
    throw toApiError(error, "Failed to unmerge table group.");
  }
};
